import { useMediaQuery } from "@mui/material";
import type { Breakpoint } from "@gui/providers/breakpoints";
import {
	belowBreakpointQuery,
	LANDSCAPE_MEDIA_QUERY,
	MOBILE_MEDIA_QUERY,
	resolveResponsiveLayout,
	type ResponsiveLayout,
} from "./responsiveLayout";

export interface ResponsiveView {
	/** Which layout tree should render. */
	layout: ResponsiveLayout;
	/** Derived from `layout` — never settable on its own, so the two can't disagree. */
	isMobile: boolean;
}

/**
 * The single source of truth for device classification.
 *
 * Asks two questions — "is this a phone?" and "is it on its side?" — because width alone cannot
 * answer the first: a landscape phone is 844-956px wide. See `responsiveLayout.ts` for the queries
 * and why each clause is shaped the way it is.
 *
 * Callers that only route between a desktop and a mobile tree want `isMobile`; only the two map
 * widgets, which swap a bottom sheet for a side sheet, need the full `layout`.
 */
export const useResponsiveView = (): ResponsiveView => {
	const narrowOrShort = useMediaQuery(MOBILE_MEDIA_QUERY);
	const shortLandscape = useMediaQuery(LANDSCAPE_MEDIA_QUERY);
	const layout = resolveResponsiveLayout({ narrowOrShort, shortLandscape });
	return { layout, isMobile: layout !== "desktop" };
};

/**
 * "Narrower than this breakpoint" — for compacting ONE component inside a layout (a denser
 * toolbar, icon-only buttons), not for choosing the mobile tree: that is `useResponsiveView`'s
 * `isMobile`, which is why `md` is not accepted here. Reach for this only when the markup itself
 * changes; when only styles change, use `sx` responsive values (`{ xs: …, sm: … }`) instead.
 */
export const useBelowBreakpoint = (breakpoint: Exclude<Breakpoint, "xs" | "md">): boolean =>
	useMediaQuery(belowBreakpointQuery(breakpoint));
